import { useEffect, useState, useRef, RefObject } from "react";

export const INFINITE_CODE = {
  NO_DATA: 0,
  ERROR: -1,
};

export type InfiniteScrollCallbackArgs = {
  page?: number;
  pageSize?: number;
};

export type InfiniteScrollProps = {
  callback: (args: InfiniteScrollCallbackArgs) => Promise<number>;
  page?: number;
  pageSize?: number;
  isLoading?: boolean;
  startPage?: number;
  target?: RefObject<HTMLElement>;
};

const useInfiniteScroll = ({
  isLoading,
  callback,
  target,
  startPage = 2,
  page = 1,
  pageSize = 10,
}: InfiniteScrollProps): [
  InfiniteScrollCallbackArgs,
  { resetScroll: () => void }
] => {
  const [callbackArgs, setCallbackArgs] = useState<InfiniteScrollCallbackArgs>({
    page,
    pageSize,
  });
  const isFetchingRef = useRef(false);
  const isLastRef = useRef(false);
  const prevScrollY = useRef(0);
  const prevLoadHeight = useRef(0);

  const handleScroll = async () => {
    const scrollElement = target?.current || document.documentElement;

    const currentScrollY = target?.current
      ? target.current.scrollTop
      : window.scrollY;
    const currentHeight = target?.current
      ? target.current.clientHeight
      : window.innerHeight;
    const containerHeight = target?.current
      ? target.current.clientHeight
      : window.innerHeight;

    if (
      isLoading !== true &&
      !isFetchingRef.current &&
      !isLastRef.current &&
      scrollElement.scrollHeight > prevLoadHeight.current &&
      currentScrollY > prevScrollY.current &&
      2 * containerHeight + scrollElement.scrollTop > scrollElement.scrollHeight
    ) {
      isFetchingRef.current = true;
      const result = await callback(callbackArgs);
      if (result > INFINITE_CODE.NO_DATA) {
        prevLoadHeight.current = currentHeight;
        prevScrollY.current = currentScrollY;
        setCallbackArgs((prev) => ({ ...prev, page: (prev?.page ?? 0) + 1 }));
        if (result < pageSize) {
          isLastRef.current = true;
        }
      } else if (result === INFINITE_CODE.NO_DATA) {
        isLastRef.current = true;
      } else if (result === INFINITE_CODE.ERROR) {
        isLastRef.current = true;
      }
      isFetchingRef.current = false;
    }
  };

  const resetScroll = () => {
    prevScrollY.current = 0;
    prevLoadHeight.current = 0;
    setCallbackArgs({ page: startPage, pageSize });
    isLastRef.current = false;
  };

  useEffect(() => {
    if (target?.current) {
      target.current.addEventListener("scroll", handleScroll);
      return () => target.current?.removeEventListener("scroll", handleScroll);
    } else {
      window.addEventListener("scroll", handleScroll);
      return () => window.removeEventListener("scroll", handleScroll);
    }
  }, [handleScroll]);

  return [callbackArgs, { resetScroll }];
};

export default useInfiniteScroll;
